import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import useCalendarStore from '../../store/useCalendarStore';
import { toast } from 'sonner';
import {
    Calendar, Users, UserCog, Database, GraduationCap,
    Settings, Moon, Sun, LogOut, Link2, Cpu, RefreshCw,
    ChevronsRight, ChevronsLeft, FolderTree, FileSearch2,
    LayoutDashboard, ClipboardCheck, MessageSquare, CreditCard, Shield, Sparkles,
    Pin, Zap, ZapOff, History
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { rbac, getProfile, getAccessLevel } from '../../lib/rbac';
import { supabase } from '../../lib/supabase';
import api from '../../api';
import { useGlobalShortcuts, useEscapeHierarchy } from '../../hooks/useKeyboardShortcuts';
import ShortcutCheatSheet from '../ShortcutCheatSheet';
import ReportBugModal from '../ReportBugModal';
import { SchedulingJobsPoller, SchedulingJobsSidebar } from '../SchedulingJobsWidget';
import { Bug } from 'lucide-react';

const PANELS = [
    { id: 'main', icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard/main' },
    { id: 'calendar', icon: Calendar, label: 'Scheduler', path: '/dashboard/calendar', minLevel: 9 },
    { id: 'entities', icon: Database, label: 'Master Data', path: '/dashboard/entities', minLevel: 9 },
    { id: 'assignments', icon: UserCog, label: 'Assignments', path: '/dashboard/assignments', minLevel: 1 },
    { id: 'students', icon: GraduationCap, label: 'Students', path: '/dashboard/students', minLevel: 9 },
    { id: 'explorer', icon: FolderTree, label: 'Content Explorer', path: '/dashboard/explorer', minLevel: 5 },
    { id: 'inquiries', icon: MessageSquare, label: 'Inquiries Portal', path: '/dashboard/inquiries', minLevel: 1 },
    { id: 'scholarsanchor', icon: Sparkles, label: 'Benchrex Hub', path: '/dashboard/benchrex', minLevel: 10 },
    { id: 'settings', icon: Settings, label: 'Settings', path: '/dashboard/settings' },
];

export default function DashboardLayout() {
    const darkMode = useCalendarStore(s => s.darkMode);
    const setDarkMode = useCalendarStore(s => s.setDarkMode);

    const [isPinned, setIsPinned] = useState(() => {
        return localStorage.getItem('sidebar_pinned') === 'true';
    });
    const [isHovered, setIsHovered] = useState(false);
    const [hoverEnabled, setHoverEnabled] = useState(() => {
        return localStorage.getItem('sidebar_hover_enabled') !== 'false';
    });

    const sidebarOpen = isPinned || (hoverEnabled && isHovered);
    const navigate = useNavigate();
    const location = useLocation();

    // ─── Cheat Sheet ───
    const [cheatSheetOpen, setCheatSheetOpen] = useState(false);
    const [bugModalOpen, setBugModalOpen] = useState(false);

    // ─── Global keyboard shortcuts (G-chords + ?) ───
    const handlePanelSwitch = useCallback((path) => {
        navigate(path);
    }, [navigate]);

    const handleToggleCheatSheet = useCallback(() => {
        setCheatSheetOpen(prev => !prev);
    }, []);

    useGlobalShortcuts({
        onPanelSwitch: handlePanelSwitch,
        onToggleCheatSheet: handleToggleCheatSheet,
    });

    // ESC to close cheat sheet
    useEscapeHierarchy([
        { id: 'cheat-sheet', active: cheatSheetOpen, onEscape: () => setCheatSheetOpen(false) },
    ]);

    const profile = useMemo(() => getProfile(), []);

    const username = profile.name || profile.admin_id || localStorage.getItem('admin_id') || 'ScholarsAnchor';

    useEffect(() => {
        const checkAuth = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                console.warn('No active session found in DashboardLayout, redirecting...');
                localStorage.removeItem('admin_profile');
                navigate('/login');
            }
        };
        checkAuth();

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'SIGNED_OUT') {
                localStorage.removeItem('admin_profile');
                navigate('/login');
            }
        });

        return () => subscription.unsubscribe();
    }, [navigate]);

    useEffect(() => {
        localStorage.setItem('sidebar_pinned', isPinned ? 'true' : 'false');
    }, [isPinned]);

    useEffect(() => {
        localStorage.setItem('sidebar_hover_enabled', hoverEnabled ? 'true' : 'false');
    }, [hoverEnabled]);

    const [teamName, setTeamName] = useState('ScholarsAnchor Portal');
    const [settings, setLocalSettings] = useState(null);
    const setStoreSettings = useCalendarStore(s => s.setSettings);

    useEffect(() => {
        const fetchTeamInfo = async () => {
            if (!profile?.team_id) return;

            try {
                // Fetch Team and Settings via the backend API instead of querying Supabase directly
                const response = await api.get('/api/team');
                const teamData = response.data;

                if (teamData) {
                    if (teamData.name) {
                        setTeamName(teamData.name);
                    }
                    
                    if (teamData.settings) {
                        setLocalSettings(teamData.settings);
                        setStoreSettings(teamData.settings);
                    } else {
                        const defaults = {
                            team_id: profile.team_id,
                            working_hours_start: '09:00',
                            working_hours_end: '18:00',
                            max_hours_per_day: 8,
                            slot_duration_minutes: 30,
                            auto_sync: true
                        };
                        setLocalSettings(defaults);
                        setStoreSettings(defaults);
                    }
                }
            } catch (err) {
                console.error("Failed to fetch team info in DashboardLayout:", err);
            }
        };

        fetchTeamInfo();
    }, [profile.team_id, setStoreSettings]);

    useEffect(() => {
        document.documentElement.classList.toggle('dark', darkMode);
        localStorage.setItem('theme', darkMode ? 'dark' : 'light');
    }, [darkMode]);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const mobileSidebarOpen = useCalendarStore(s => s.mobileSidebarOpen);
    const setMobileSidebarOpen = useCalendarStore(s => s.setMobileSidebarOpen);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Determine active panel from current path
    const level = useMemo(() => getAccessLevel(), []);
    const visiblePanels = useMemo(() => {
        let panels = PANELS.filter(p => !p.minLevel || level <= p.minLevel);
        if (level === 7) {
            panels = panels.filter(p => ['students', 'calendar', 'settings'].includes(p.id));
        }
        return panels;
    }, [level]);
    const activePanel = visiblePanels.find(p => p.path && location.pathname.startsWith(p.path))?.id || (level === 7 ? 'students' : 'main');

    // Guard manual URL access based on minLevel and role permissions
    useEffect(() => {
        if (level === 7 && (location.pathname === '/dashboard' || location.pathname === '/dashboard/main' || location.pathname === '/dashboard/')) {
            navigate('/dashboard/students', { replace: true });
            return;
        }

        const currentPanel = PANELS.find(p => p.path && location.pathname.startsWith(p.path));
        if (currentPanel) {
            const isAllowed = visiblePanels.some(p => p.id === currentPanel.id);
            if (!isAllowed) {
                toast.error('Unauthorized access to this panel');
                if (level === 7) {
                    navigate('/dashboard/students', { replace: true });
                } else {
                    navigate('/dashboard/main', { replace: true });
                }
            }
        }
    }, [location.pathname, level, navigate, visiblePanels]);

    const handleSignOut = async () => {
        await supabase.auth.signOut();
        localStorage.removeItem('admin_profile');
        toast.success('Signed out');
        navigate('/login');
    };

    const hasCustomHeader = ['calendar', 'scholarsanchor', 'entities'].includes(activePanel);

    return (
        <div className="flex flex-col md:flex-row h-screen overflow-hidden bg-white dark:bg-zinc-950" data-testid="dashboard">
            {/* Mobile Top Bar - Optimized for premium feel - Hidden if panel has custom header */}
            {isMobile && !hasCustomHeader && (
                <div className="flex items-center justify-between px-4 h-14 border-b border-zinc-200 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl sticky top-0 z-[60]">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setMobileSidebarOpen(true)}
                        className="w-10 h-10 flex items-center justify-center -ml-1 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-xl transition-all active:scale-90"
                    >
                        <ChevronsRight className={cn("w-5 h-5 transition-transform duration-300", mobileSidebarOpen && "rotate-180")} />
                    </button>
                    <div className="flex flex-col">
                        <span className="text-[10px] font-black tracking-[0.2em] text-blue-600 dark:text-blue-500 uppercase leading-none opacity-90">
                            ScholarsAnchor
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setDarkMode(!darkMode)}
                        className="w-9 h-9 flex items-center justify-center text-zinc-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-all active:scale-95 border border-zinc-200/50 dark:border-zinc-800/50 bg-white dark:bg-zinc-900"
                    >
                        {darkMode ? <Sun className="w-4 h-4 text-amber-500" /> : <Moon className="w-4 h-4 text-blue-500" />}
                    </button>
                    <button 
                        onClick={() => navigate('/dashboard/settings')}
                        className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold border border-blue-400/50 shadow-md shadow-blue-500/10 active:scale-90 transition-transform"
                    >
                        <span className="text-xs">{username.charAt(0).toUpperCase()}</span>
                    </button>
                </div>
            </div>
            )}

            {/* Sidebar (Drawer on mobile, Sidebar on desktop) */}
            <>
                {/* Mobile Backdrop - Ultra smooth Blur + Fade */}
                {isMobile && (
                    <div
                        className={cn(
                            "fixed inset-0 bg-zinc-950/40 backdrop-blur-sm z-[70] transition-all duration-300 ease-in-out",
                            mobileSidebarOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                        )}
                        onClick={() => setMobileSidebarOpen(false)}
                    />
                )}

                <div
                    className={cn(
                        "flex flex-col h-full border-r border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-950 py-4 shrink-0 overflow-hidden",
                        isMobile
                            ? "fixed inset-y-0 left-0 z-[80] shadow-[20px_0_60px_-15px_rgba(0,0,0,0.3)] transition-transform duration-300 ease-out"
                            : "relative transition-[width] duration-300 ease-in-out"
                    )}
                    style={{
                        width: isMobile ? 280 : (sidebarOpen ? 240 : 72),
                        transform: isMobile ? (mobileSidebarOpen ? 'translateX(0)' : 'translateX(-100%)') : 'none',
                        willChange: 'width'
                    }}
                    onMouseEnter={() => !isMobile && setIsHovered(true)}
                    onMouseLeave={() => !isMobile && setIsHovered(false)}
                    data-testid="sidebar"
                >
                    {/* Header in sidebar (Mobile) */}
                    {isMobile && (
                        <div className="flex items-center justify-between px-5 mb-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 shrink-0">
                                    <img src={darkMode ? "/logo.png" : "/logo_black.png"} alt="ScholarsAnchor Logo" className="w-full h-full object-contain" />
                                </div>
                                <div className="flex flex-col items-start leading-none">
                                    <span className="text-lg font-black tracking-tight text-zinc-900 dark:text-zinc-50 uppercase">
                                        Scholars
                                    </span>
                                    <span className="text-[0.6rem] font-black tracking-[0.4em] text-blue-600 dark:text-blue-500 uppercase -mt-0.5">
                                        Anchor
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() => setMobileSidebarOpen(false)}
                                className="w-8 h-8 flex items-center justify-center hover:bg-zinc-200 dark:hover:bg-zinc-800 rounded-lg transition-colors"
                            >
                                <ChevronsLeft className="w-5 h-5 text-zinc-400" />
                            </button>
                        </div>
                    )}

                    {!isMobile && (
                        <div className={cn(
                            "mb-8 flex items-center transition-all duration-300",
                            sidebarOpen ? "px-5" : "px-0 justify-center"
                        )}>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 shrink-0">
                                    <img src={darkMode ? "/logo.png" : "/logo_black.png"} alt="ScholarsAnchor Logo" className="w-full h-full object-contain" />
                                </div>
                                {sidebarOpen && (
                                    <div className="flex flex-col items-start leading-none">
                                        <span className="text-lg font-black tracking-tight text-zinc-900 dark:text-zinc-50 uppercase">
                                            Scholars
                                        </span>
                                        <span className="text-[0.6rem] font-black tracking-[0.4em] text-blue-600 dark:text-blue-500 uppercase -mt-0.5">
                                            Anchor
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Nav items */}
                    <div className="flex flex-col gap-1.5 flex-1 px-3">
                        {visiblePanels.map((panel) => {
                            const Icon = panel.icon;
                            const isActive = activePanel === panel.id;
                            const isDisabled = panel.disabled;
                            return (
                                <button
                                    key={panel.id}
                                    className={cn(
                                        "flex items-center gap-3 rounded-xl transition-all h-11 md:h-10 group relative",
                                        (sidebarOpen || isMobile) ? "px-4 justify-start" : "px-0 justify-center",
                                        isActive && !isDisabled && "bg-blue-600 text-white shadow-[0_10px_20px_-5px_rgba(37,99,235,0.4)] dark:shadow-[0_10px_20px_-5px_rgba(37,99,235,0.2)] font-bold",
                                        !isActive && !isDisabled && "text-zinc-500 dark:text-zinc-400 hover:bg-zinc-200/50 dark:hover:bg-zinc-800/50",
                                        isDisabled && "opacity-40 cursor-not-allowed"
                                    )}
                                    onClick={() => {
                                        if (isDisabled) {
                                            toast.info(`${panel.label} is disabled`);
                                            return;
                                        }
                                        navigate(panel.path);
                                        if (isMobile) setMobileSidebarOpen(false);
                                    }}
                                    data-testid={`panel-btn-${panel.id}`}
                                    title={panel.label}
                                >
                                    <Icon className={cn(
                                        "w-5 h-5 md:w-[1.125rem] md:h-[1.125rem] flex-shrink-0 transition-transform duration-200 group-hover:scale-110",
                                        isActive && !isDisabled ? "text-white" : ""
                                    )} />
                                    {(sidebarOpen || isMobile) && <span className="text-sm md:text-xs truncate">{panel.label}</span>}
                                    {isActive && !sidebarOpen && !isMobile && (
                                        <div className="absolute left-0 w-1 h-6 bg-blue-600 rounded-r-full" />
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* User Profile / Status (Small) */}
                    {(sidebarOpen || isMobile) && (
                        <button 
                            onClick={() => {
                                navigate('/dashboard/settings');
                                if (isMobile) setMobileSidebarOpen(false);
                            }}
                            className="px-5 py-3 mx-3 mb-4 rounded-xl bg-white/50 dark:bg-zinc-900/50 border border-zinc-200/50 dark:border-zinc-800/50 hover:bg-white dark:hover:bg-zinc-800 transition-all text-left group"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-xs group-hover:scale-110 transition-transform">
                                    {teamName.charAt(0).toUpperCase()}
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <span className="text-[11px] font-bold text-zinc-800 dark:text-zinc-100 truncate group-hover:text-blue-600 transition-colors">{teamName}</span>
                                    <span className="text-[9px] text-zinc-400 font-medium uppercase tracking-tight">
                                        {level === 1 ? 'Super Admin' : (level === 2 ? 'Branch Admin' : 'Admin')}
                                    </span>
                                </div>
                            </div>
                        </button>
                    )}


                    {/* ── Scheduling Jobs Sidebar ── */}
                    {(sidebarOpen || isMobile) && (
                        <SchedulingJobsSidebar />
                    )}

                    {/* Bottom actions */}
                    <div className="flex flex-col gap-1 px-3 border-t border-zinc-200 dark:border-zinc-800 pt-5 mt-auto">
                        {!isMobile && (
                            <button
                                className={cn("flex items-center gap-3 rounded-xl h-10 transition-colors text-zinc-500 dark:text-zinc-400 hover:bg-zinc-200/50 dark:hover:bg-zinc-800/50", sidebarOpen ? "px-4 justify-start" : "px-0 justify-center")}
                                onClick={() => setDarkMode(!darkMode)}
                                title={darkMode ? 'Light mode' : 'Dark mode'}
                            >
                                {darkMode ? <Sun className="w-[1.125rem] h-[1.125rem] flex-shrink-0" /> : <Moon className="w-[1.125rem] h-[1.125rem] flex-shrink-0" />}
                                {sidebarOpen && <span className="text-xs font-semibold">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>}
                            </button>
                        )}
                        <button
                            className={cn(
                                "flex items-center gap-3 rounded-xl h-11 md:h-10 transition-all text-zinc-500 dark:text-zinc-400 hover:bg-blue-50 dark:hover:bg-blue-950/20 hover:text-blue-600",
                                (sidebarOpen || isMobile) ? "px-4 justify-start" : "px-0 justify-center"
                            )}
                            onClick={() => setBugModalOpen(true)}
                            title="Report a Bug"
                        >
                            <Bug className="w-5 h-5 md:w-[1.125rem] md:h-[1.125rem] flex-shrink-0" />
                            {(sidebarOpen || isMobile) && <span className="text-sm md:text-xs font-semibold">Report Bug</span>}
                        </button>
                        <button
                            className={cn("flex items-center gap-3 rounded-xl h-11 md:h-10 transition-all text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20", (sidebarOpen || isMobile) ? "px-4 justify-start" : "px-0 justify-center")}
                            onClick={handleSignOut}
                            title="Sign Out"
                        >
                            <LogOut className="w-5 h-5 md:w-[1.125rem] md:h-[1.125rem] flex-shrink-0" />
                            {(sidebarOpen || isMobile) && <span className="text-sm md:text-xs font-semibold">Sign Out</span>}
                        </button>
                        {!isMobile && (
                            <button
                                className={cn(
                                    "flex items-center rounded-xl h-9 transition-all duration-300 mt-2",
                                    sidebarOpen
                                        ? "px-4 justify-start gap-3 bg-zinc-200/50 dark:bg-zinc-800/50 text-zinc-900 dark:text-zinc-100"
                                        : "px-0 justify-center text-zinc-400 hover:bg-zinc-200/50 dark:hover:bg-zinc-800/50"
                                )}
                                onClick={() => {
                                    if (sidebarOpen) {
                                        setIsPinned(!isPinned);
                                    } else {
                                        setHoverEnabled(!hoverEnabled);
                                    }
                                }}
                                title={sidebarOpen ? (isPinned ? 'Unpin sidebar' : 'Pin sidebar') : (hoverEnabled ? 'Disable expand on hover' : 'Enable expand on hover')}
                            >
                                {sidebarOpen ? (
                                    <Pin className={cn("w-4 h-4 flex-shrink-0 transition-transform", isPinned && "rotate-45 text-blue-600")} />
                                ) : (
                                    hoverEnabled ? <Zap className="w-4 h-4 flex-shrink-0" /> : <ZapOff className="w-4 h-4 flex-shrink-0 opacity-40" />
                                )}
                                <span className={cn(
                                    "text-[0.6875rem] font-bold uppercase tracking-widest truncate transition-all duration-300",
                                    sidebarOpen ? "opacity-100 w-auto ml-3" : "opacity-0 w-0"
                                )}>
                                    {sidebarOpen ? "Pin" : "Disable expand on hover"}
                                </span>
                                {!sidebarOpen && (
                                    <span className="sr-only">
                                        {hoverEnabled ? 'Disable expand on hover' : 'Enable expand on hover'}
                                    </span>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </>

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden bg-white dark:bg-zinc-900 relative">
                <div className="flex-1 flex flex-col overflow-hidden pb-16 md:pb-0">
                    <Outlet />
                </div>

                {/* Mobile Bottom Navigation - Premium App Feel */}
                {isMobile && (
                    <div className="fixed bottom-0 left-0 right-0 h-16 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border-t border-zinc-200 dark:border-zinc-800 flex items-center justify-around px-2 z-50">
                        {visiblePanels.filter(p => ['main', 'calendar', 'entities', 'scholarsanchor', 'settings'].includes(p.id)).map((panel) => {
                            const Icon = panel.icon;
                            const isActive = activePanel === panel.id;
                            return (
                                <button
                                    key={panel.id}
                                    onClick={() => navigate(panel.path)}
                                    className={cn(
                                        "flex flex-col items-center gap-1 flex-1 py-1 transition-all relative",
                                        isActive ? "text-blue-600 dark:text-blue-400" : "text-zinc-400"
                                    )}
                                >
                                    <Icon className={cn("w-5 h-5 transition-transform", isActive && "scale-110")} />
                                    <span className="text-[9px] font-black uppercase tracking-tighter">{panel.label}</span>
                                    {isActive && (
                                        <motion.div
                                            layoutId="bottom-nav-indicator"
                                            className="absolute -top-1 w-8 h-1 bg-blue-600 rounded-full"
                                            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Shortcut Cheat Sheet */}
            {!isMobile && <ShortcutCheatSheet open={cheatSheetOpen} onClose={() => setCheatSheetOpen(false)} />}

            {/* Bug Report Modal */}
            <ReportBugModal open={bugModalOpen} onClose={() => setBugModalOpen(false)} />

            {/* Single global poller + floating overlay (visible from any page) */}
            <SchedulingJobsPoller />
        </div>
    );
}
